import { forwardRef, useImperativeHandle, useRef } from 'react';
import './style.scss';
import uniq from 'lodash/uniq';
import { TranslationService } from '../../../services/translation.service';
import { ClientSideStorageItems } from '../../../services/client-side-storage.service';
import type { LocationType } from '../../../models/location-type.enum';
import type { IClientSideStorageItem } from '../../../models/client-side-storage-item.model';
import LocationAutocomplete, { type ILocationAutocompleteSelection } from '../LocationAutocomplete';
import { LocationAutocompleteSelectionType } from '../LocationAutocomplete/location-autocomplete-selection-type.enum';
import ValidationInfo from '../../../validation/validation-info';
import { type IAutocomplete } from '../AutocompleteBase';
import arrowIconDark from '../../../../assets/icons/arrow-up-arrow-down-gray-900.svg';
import DataUrlHelper from '../../../helpers/data-url.helper';
import { AnalyticsService } from '../../../services/analytics-service';
import type IValidatable from '../validatable.interface';
import type { IServerValidationError } from '../validatable.interface';
import { AutocompleteLabelPosition } from '../AutocompleteBase/autocomplete-base.enum';

export interface IFromToSelector extends IValidatable {
  addSelectedItemsToAutocompleteHistory: () => void;
}

export interface IFromToSelectorState {
  value: {
    fromLocation: ILocationAutocompleteSelection | undefined;
    toLocation: ILocationAutocompleteSelection | undefined;
  };
  validationInfo: ValidationInfo;
}

interface IFromToSelectorProps {
  id?: string;
  locationTypes: LocationType[];
  placeholderText?: string;
  localStorageItemFrom?: IClientSideStorageItem;
  localStorageItemTo?: IClientSideStorageItem;
  enableCurrentPosition?: boolean;
  analyticsCategory?: string | undefined;
  state: IFromToSelectorState;
  onChange?: ((value: { fromLocation: ILocationAutocompleteSelection | undefined; toLocation: ILocationAutocompleteSelection | undefined }) => void) | undefined;
  onFocus?: (() => void) | undefined;
}

export default forwardRef<IValidatable, IFromToSelectorProps>(function FromToSelector(
  {
    id = '',
    locationTypes,
    placeholderText = '',
    localStorageItemFrom = ClientSideStorageItems.LocalStorage.AutocompleteHistoryFrom,
    localStorageItemTo = ClientSideStorageItems.LocalStorage.AutocompleteHistoryTo,
    enableCurrentPosition = false,
    analyticsCategory = '',
    state,
    onChange,
    onFocus,
  }: IFromToSelectorProps,
  ref
) {
  // === REFS ===
  const fromLocationAutocompleteRef = useRef<IAutocomplete & IValidatable>(null!);
  const toLocationAutocompleteRef = useRef<IAutocomplete & IValidatable>(null!);

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const from = fromLocationAutocompleteRef.current.checkValidity();
      const to = toLocationAutocompleteRef.current.checkValidity();
      return new ValidationInfo('', [], {
        fromLocation: from,
        toLocation: to,
      });
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo('', [], {
        fromLocation: fromLocationAutocompleteRef.current.handleServerValidationErrors(errors),
        toLocation: toLocationAutocompleteRef.current.handleServerValidationErrors(errors),
      });
    },
    addSelectedItemsToAutocompleteHistory() {
      if (state.value.fromLocation) fromLocationAutocompleteRef.current.addSelectedItemToHistory(state.value.fromLocation);
      if (state.value.toLocation) toLocationAutocompleteRef.current.addSelectedItemToHistory(state.value.toLocation);
    },
  }));

  // === EVENT HANDLERS ===
  function handleSwitchClick() {
    onChange?.({ fromLocation: state.value.toLocation, toLocation: state.value.fromLocation });
    if (analyticsCategory) {
      AnalyticsService.addEvent(analyticsCategory, 'Klick på omvänd resa', '');
    }
  }

  return (
    <div className="from-to-selector position-relative">
      <div className={`rounded border border-gray-700 flex-wrap from-to-selector__autocompletes d-flex ${state.validationInfo.isInvalid ? 'border-red' : ''}`}>
        <div className={`w-100 ${state.validationInfo.children.fromLocation?.isInvalid ? 'from-to-selector__location-autocomplete--invalid' : ''}`}>
          <LocationAutocomplete
            ref={fromLocationAutocompleteRef}
            id={`${id}-from`}
            labelText={TranslationService.get('common.from')}
            labelPosition={AutocompleteLabelPosition.inside}
            placeholderText={placeholderText}
            locationTypes={locationTypes}
            historyProps={{
              localStorageItem: localStorageItemFrom,
              historyLabel: TranslationService.get('common.autocomplete.suggestionlabel.from'),
            }}
            state={{
              value: state.value.fromLocation,
              validationInfo: state.validationInfo.children.fromLocation ?? new ValidationInfo(),
            }}
            required={true}
            enableCurrentPosition={enableCurrentPosition && state.value.toLocation?.type !== LocationAutocompleteSelectionType.currentPosition}
            ariaDescribedBy={state.validationInfo.children.fromLocation?.isInvalid ? 'from-to-selector-error' : undefined}
            showValidationMessages={false}
            onSelect={(value) => {
              onChange?.({ fromLocation: value, toLocation: state.value.toLocation });
            }}
            onFocus={onFocus}
          />
        </div>
        <div className="from-to-selector__change-direction ms-2 me-4 justify-content-center align-items-center border-bottom border-gray-700 position-relative">
          <button type="button" onClick={handleSwitchClick} className="btn--unstyled" title={TranslationService.get('common.changedirection')}>
            <img src={DataUrlHelper.encode(arrowIconDark)} alt={TranslationService.get('common.changedirection')} />
          </button>
        </div>
        <div className={`w-100 ${state.validationInfo.children.toLocation?.isInvalid ? 'from-to-selector__location-autocomplete--invalid' : ''}`}>
          <LocationAutocomplete
            ref={toLocationAutocompleteRef}
            id={`${id}-to`}
            labelText={TranslationService.get('common.to')}
            labelPosition={AutocompleteLabelPosition.inside}
            placeholderText={placeholderText}
            locationTypes={locationTypes}
            historyProps={{
              localStorageItem: localStorageItemTo,
              historyLabel: TranslationService.get('common.autocomplete.suggestionlabel.to'),
            }}
            state={{
              value: state.value.toLocation,
              validationInfo: state.validationInfo.children.toLocation ?? new ValidationInfo(),
            }}
            required={true}
            enableCurrentPosition={enableCurrentPosition && state.value.fromLocation?.type !== LocationAutocompleteSelectionType.currentPosition}
            ariaDescribedBy={state.validationInfo.children.toLocation?.isInvalid ? 'from-to-selector-error' : undefined}
            showValidationMessages={false}
            onSelect={(value) => {
              onChange?.({ fromLocation: state.value.fromLocation, toLocation: value });
            }}
            onFocus={onFocus}
          />
        </div>
      </div>
      <div role="alert">
        {state.validationInfo.isInvalid && (
          <div id="from-to-selector-error" className="invalid-feedback d-block">
            {uniq(state.validationInfo.children.fromLocation.errors.concat(state.validationInfo.children.toLocation.errors)).map((error) => (
              <div key={error}>{error}</div>
            ))}
          </div>
        )}
      </div>
      {/* Add when needed */}
      {/* {!isInEditMode && (
        <div className={`d-block p-2 rounded border bg-white ${layoutMdRow ? 'd-md-none' : ''} ${layoutLgColumn ? 'd-lg-block' : ''}`} role="button" onClick={() => setEditMode(true)}>
          <div className="my-1">
            <span className="from-to-selector__data-item-label fw-bold d-inline-block">{TranslationService.get('common.from')}:</span>
            <span>{fromLocation ? fromLocation.name : ''}</span>
          </div>
          <div className="my-1">
            <span className="from-to-selector__data-item-label fw-bold d-inline-block">{TranslationService.get('common.to')}:</span>
            <span>{toLocation ? toLocation.name : ''}</span>
          </div>
          <div className="my-1">
            <span className="from-to-selector__data-item-label fw-bold d-inline-block">{TranslationService.get('common.when')}:</span>
            <span>{outputDateString.substring(0, 1).toUpperCase() + outputDateString.substring(1)}</span>
          </div>
        </div>
      )} */}
    </div>
  );
});
